import { createPortal } from 'react-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  CalendarDays,
  Castle,
  Copy,
  LayoutGrid,
  List,
  Loader2,
  MoreVertical,
  Play,
  Plus,
  Power,
  Search,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Etiqueta } from '@/components/ui/etiqueta';
import { EstadoError, EstadoVacio } from '@/components/ui/estado-vacio';
import { Input } from '@/components/ui/input';
import { SelectNativo } from '@/components/ui/select-nativo';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tarjeta } from '@/features/compite/ui/tarjeta';
import { Pildoras, Segmentado } from '@/features/compite/ui/pildoras';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { ToastAction } from '@/components/ui/toast';
import {
  AVISO_SOLO_MIRA,
  atributosSoloMira,
  useSoporteSoloMira,
} from '@/features/mini-prixia/soporte-solo-mira';
import { useToast } from '@/lib/hooks/use-toast';
import { cn } from '@/lib/utils';
import {
  cantidadDe,
  formatoCantidad,
  formatoFecha,
  formatoFechaYHora,
  formatoNumero,
} from '@/lib/utils/formato-precio';
import { strategyApplicationsApi } from '../services/strategy-applications-api';
import type {
  AppliedStrategy,
  RunSummary,
  StrategyKind,
} from '../types/strategy-applications.types';
import type { DestinoHoy, FiltroBandeja } from '../types/price-proposals.types';
import {
  PERMISO_INICIAL,
  PermisoDeEstrategia,
  permisoEnPalabras,
  type Permiso,
} from './permiso-de-estrategia';
import { NuevaEstrategiaDialog } from './nueva-estrategia/nueva-estrategia-dialog';
import {
  borradorDesdeLaEstrategia,
  estaVencida,
  loQueNoSeCopia,
} from './nueva-estrategia/borrador';
import { FichaEstrategiaDialog, ResumenCorrida } from './ficha-estrategia';
import { ProductosDeLaEstrategiaDialog } from './productos-de-la-estrategia-dialog';
import { aQuienEnLaTarjeta, comoTomaEnLaTarjeta } from '../lib/como-toma-los-productos';
import { SelectorDeFechas } from './selector-de-fechas';
import { CUALQUIER_FECHA, enElRango, type RangoDeFechas } from '../lib/rango-de-fechas';
import {
  cadaCuantoRevisa,
  fotoDeHoy,
  fotosCompletas,
  historialDeEstrategiaEnPalabras,
  lineaDeQuien,
} from '../lib/historial-de-estrategia-en-palabras';
import { HistorialDeCambios } from './historial-de-cambios';
import { METODOS } from './nueva-estrategia/paso-2-metodo';
import { usePuedeCrearEstrategias } from './nueva-estrategia/puede-crear';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { corridasApi } from '../services/corridas-api';
import type { UltimasPorEstrategia } from '../types/corridas.types';
import { TiraDeCorridas } from './corridas/tira-de-corridas';
import { VistaDeLaEstrategia } from './corridas/vista-de-la-estrategia';
import { filtroInicialPorFechas, VistaPorFechas } from './corridas/vista-por-fechas';
import type { FiltroDeCorridas } from './corridas/filtros-de-corridas';
import { BOTON_DEGRADADO } from '@/features/compite/ui/boton-degradado';

/**
 * Estrategias aplicadas — Etapa 4, Tarea 7 (plan 167 §7).
 *
 * Es lo que faltaba para poder usar el circuito: aplicar una estrategia sobre un
 * grupo de productos, verla, correrla y darla de baja.
 *
 * EL SOLAPAMIENTO SE AVISA ANTES
 * ------------------------------
 * Decisión #8 del spec: un producto tiene una sola estrategia. Si el grupo nuevo
 * alcanza productos que ya tiene otra, hay que elegir qué hacer — y no se puede
 * crear sin elegir. Dos estrategias peleándose un precio es peor que un error.
 */

/**
 * Qué número pide cada método (verificado en `price-calculator.service.ts`
 * el 23-sep): los demás no leen nada de `strategyConfig`.
 */
const CONFIG_CAMPO: Partial<
  Record<
    StrategyKind,
    { key: 'undercutPercent' | 'targetMargin' | 'discountPercent'; label: string; default: number }
  >
> = {
  UNDERCUT_PERCENT: { key: 'undercutPercent', label: 'Cuánto por debajo (%)', default: 5 },
  MARGIN_TARGET: {
    key: 'targetMargin',
    label: 'Margen objetivo sobre el precio de venta (%)',
    default: 25,
  },
  // El backend no tiene valor por defecto para éste (plan 3, P3-D1): el 10 es
  // sólo el punto de partida del campo, y siempre se manda.
  DISCOUNT_PERCENT: { key: 'discountPercent', label: 'Cuánto bajar (%)', default: 10 },
};

/** El texto que va al lado del nombre del método en la tarjeta. */
function configTexto(e: AppliedStrategy): string | null {
  const campo = CONFIG_CAMPO[e.strategy];
  if (!campo) return null;
  const valor = e.strategyConfig?.[campo.key];
  // «12,5 %», no «12.5 %» (auditoría M4).
  if (typeof valor === 'number') return `${formatoNumero(valor)} %`;
  // El quinto método no tiene valor por defecto en el backend (plan 3, P3-D1).
  if (campo.key === 'discountPercent') return '—';
  return `${formatoNumero(campo.default)} % (por defecto)`;
}

/** Spec 294 (D-R4-5): la frase vive con el historial, que la usa para decir qué cambió. */
const cuando = (e: AppliedStrategy): string => cadaCuantoRevisa(e);

/** Agregados del 29-sep (A1): los cuatro estados que se filtran, con su número. */
export type EstadoDeEstrategia = 'ACTIVA' | 'APAGADA' | 'VENCIDA';
type EstadoDelFiltro = 'TODAS' | EstadoDeEstrategia;
type OrdenDeEstrategias = 'reciente' | 'vieja' | 'nombre' | 'esperan';
type Vista = 'tarjetas' | 'lista' | 'fechas';

/** Una vencida no es «apagada» (Nº 9): tiene su propio estado. */
export function estadoDeLaEstrategia(e: AppliedStrategy): EstadoDeEstrategia {
  return estaVencida(e) ? 'VENCIDA' : e.isActive ? 'ACTIVA' : 'APAGADA';
}

export interface FiltroDeEstrategias {
  q: string;
  estado: EstadoDelFiltro;
  /** Lote B2 (punto 7): el calendario, en días de Bolivia. */
  creada: RangoDeFechas;
  orden: OrdenDeEstrategias;
}

export const FILTRO_INICIAL: FiltroDeEstrategias = {
  q: '',
  estado: 'TODAS',
  creada: CUALQUIER_FECHA,
  orden: 'reciente',
};

const sinTildes = (t: string) =>
  t
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const creadaEn = (e: AppliedStrategy) => (e.createdAt ? new Date(e.createdAt).getTime() : NaN);

/**
 * El buscador, la fecha de creación, el estado y el orden de la pestaña (A1),
 * en el navegador: la lista ya viene entera. El número de cada estado cuenta
 * lo que dejaron el buscador y la fecha. Sin fecha (un servidor viejo), sólo
 * pasa con «cualquier fecha». Lote B2: la fecha, del calendario, cortando en la
 * medianoche de Bolivia (no en la de la computadora).
 */
export function filtrarEstrategias(
  lista: AppliedStrategy[],
  f: FiltroDeEstrategias
): { visibles: AppliedStrategy[]; porEstado: Record<EstadoDelFiltro, number> } {
  const q = sinTildes(f.q.trim());
  const pasan = lista.filter((e) => {
    if (q && !sinTildes(e.name).includes(q)) return false;
    return enElRango(e.createdAt, f.creada);
  });
  const porEstado: Record<EstadoDelFiltro, number> = {
    TODAS: pasan.length,
    ACTIVA: 0,
    APAGADA: 0,
    VENCIDA: 0,
  };
  for (const e of pasan) porEstado[estadoDeLaEstrategia(e)]++;
  const reciente = (a: AppliedStrategy, b: AppliedStrategy) =>
    (Number.isNaN(creadaEn(b)) ? -Infinity : creadaEn(b)) -
    (Number.isNaN(creadaEn(a)) ? -Infinity : creadaEn(a));
  const orden: Record<OrdenDeEstrategias, (a: AppliedStrategy, b: AppliedStrategy) => number> = {
    reciente,
    vieja: (a, b) =>
      (Number.isNaN(creadaEn(a)) ? Infinity : creadaEn(a)) -
      (Number.isNaN(creadaEn(b)) ? Infinity : creadaEn(b)),
    nombre: (a, b) => a.name.localeCompare(b.name, 'es'),
    esperan: (a, b) => b.propuestasPendientes - a.propuestasPendientes || reciente(a, b),
  };
  const visibles = pasan
    .filter((e) => f.estado === 'TODAS' || estadoDeLaEstrategia(e) === f.estado)
    .sort(orden[f.orden]);
  return { visibles, porEstado };
}

/**
 * La última vista elegida (A1). Sólo una comodidad: sin almacenamiento, la
 * lista — la tabla de la maqueta de Compite (15-oct; antes, las tarjetas).
 */
const CLAVE_DE_LA_VISTA = 'prixia.estrategias.vista';
function vistaGuardada(): Vista {
  try {
    const v = localStorage.getItem(CLAVE_DE_LA_VISTA);
    return v === 'tarjetas' || v === 'fechas' ? v : 'lista';
  } catch {
    return 'lista';
  }
}
function guardarVista(v: Vista) {
  try {
    localStorage.setItem(CLAVE_DE_LA_VISTA, v);
  } catch {
    // Sin almacenamiento (ventana privada, bloqueado): la próxima vez, la lista.
  }
}

/** Ronda de R3: el 500 de «Revisar ahora» cuando no pudo abrir su corrida (el texto del servidor). */
const NO_SE_PUDO_EMPEZAR = 'No se pudo empezar. Probá de nuevo.';

/**
 * Ronda del QA de R4: por qué «Revisar ahora» está deshabilitado para un rol que
 * sólo ve (el mismo texto que el 403 del servidor).
 */
const NO_PUEDE_CORRER = 'Tu rol puede ver las estrategias, pero no revisarlas.';

/**
 * Compite (rediseño del 14-oct): «Revisar ahora» deshabilitado dice siempre por
 * qué, no sólo por el rol. El permiso va primero: es lo único que no se arregla
 * desde esta pantalla.
 */
const ESTA_APAGADA = 'Está apagada: encendela para revisarla.';
const YA_VENCIO = 'Ya venció: no vuelve a revisar.';

/**
 * `onIrA` (auditoría I1): «N esperan tu decisión» lleva a Revisar precios (antes
 * «Por decidir») ya filtrado por esa estrategia, sin recargar la página (el mismo
 * `irA` que usa Decisiones).
 */
export function AppliedStrategiesTab({
  onIrA,
  refreshKey,
  onTotal,
  enCompite = false,
  destinoDeLaAccion = null,
}: {
  onIrA?: (
    destino: DestinoHoy,
    strategyApplicationId: string | null,
    filtro?: FiltroBandeja
  ) => void;
  /** Spec 275 (R3): «Recargar» de la franja — vuelve a pedir la lista y la tira. */
  refreshKey?: number;
  /**
   * Compite › Estrategia (rediseño del 14-oct): cuántas estrategias hay, para el
   * contador de la pestaña. Se avisa al cargar; quien la pasa la memoriza.
   */
  onTotal?: (n: number) => void;
  /**
   * Compite › Estrategia, como la maqueta: «Crear una estrategia» va al
   * encabezado de la página (`destinoDeLaAccion`), no a la barra de la lista.
   */
  enCompite?: boolean;
  destinoDeLaAccion?: HTMLElement | null;
} = {}) {
  const { toast } = useToast();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  // D5 (27-sep): crear pide un rol que pueda; la pestaña se ve igual para todos (M7).
  const puedeCrear = usePuedeCrearEstrategias();
  // Soporte («Entrar como») en una demo sólo mira: no crea, revisa, duplica ni cambia estrategias.
  const sinCambios = atributosSoloMira(useSoporteSoloMira());

  const [lista, setLista] = useState<AppliedStrategy[]>([]);
  const [cargando, setCargando] = useState(true);
  useEffect(() => {
    if (!cargando) onTotal?.(lista.length);
  }, [cargando, lista.length, onTotal]);
  // Spec 275 (R2): sólo la primera carga muestra el esqueleto.
  const yaCargo = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [corriendo, setCorriendo] = useState<string | null>(null);
  const [cambiando, setCambiando] = useState<Set<string>>(() => new Set());
  const [abierto, setAbierto] = useState(false);
  const [permisoDe, setPermisoDe] = useState<AppliedStrategy | null>(null);
  const [fichaDe, setFichaDe] = useState<AppliedStrategy | null>(null);
  const [productosDe, setProductosDe] = useState<AppliedStrategy | null>(null);
  // «Duplicar» (plan 3, Tarea 6b): el asistente con todo copiado de ésta.
  const [duplicando, setDuplicando] = useState<AppliedStrategy | null>(null);
  // Agregados del 29-sep (A1): buscar, filtrar, ordenar y cómo ver.
  const [filtro, setFiltro] = useState<FiltroDeEstrategias>(FILTRO_INICIAL);
  const [vista, setVista] = useState<Vista>(vistaGuardada);
  // Spec 275 (R2): sube después de «Revisar ahora» (la línea de tiempo se vuelve a pedir).
  const [versionDeCorridas, setVersionDeCorridas] = useState(0);
  // Spec 294 (D-R4-5): cada vez que se relee la lista (un cambio propio o
  // ajeno), el historial de la estrategia abierta se vuelve a pedir.
  const [versionDeLaLista, setVersionDeLaLista] = useState(0);
  // Ronda de arreglos (revisión T10/T11): los filtros de «Por fechas» viven acá,
  // así pasar a Tarjetas (o a la vista de una estrategia) y volver no los pierde.
  const [filtroPorFechas, setFiltroPorFechas] = useState<FiltroDeCorridas>(filtroInicialPorFechas);
  // Spec 275 (R2): la tira de cada tarjeta y la vista de una estrategia abierta.
  const [ultimas, setUltimas] = useState<UltimasPorEstrategia>({});
  const [abierta, setAbierta] = useState<{
    id: string;
    resaltar: string | null;
    cuando: string | null;
  } | null>(null);
  const { visibles, porEstado } = useMemo(() => filtrarEstrategias(lista, filtro), [lista, filtro]);
  const cambiarFiltro = (c: Partial<FiltroDeEstrategias>) => setFiltro((f) => ({ ...f, ...c }));

  const cargar = useCallback(async () => {
    if (!yaCargo.current) setCargando(true);
    setError(null);
    try {
      setLista(await strategyApplicationsApi.listar());
      setVersionDeLaLista((v) => v + 1);
      yaCargo.current = true;
      // La tira no frena la pestaña: si falla, las tarjetas van sin ella.
      corridasApi.ultimas(7).then(setUltimas, () => setUltimas({}));
    } catch (e) {
      setError(mensajeDeError(e, 'No se pudieron traer las estrategias.'));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // Spec 275 (R3): «Recargar» de la franja — la lista y la tira se vuelven a pedir.
  const refreshKeyVisto = useRef(refreshKey);
  useEffect(() => {
    if (refreshKey === undefined || refreshKey === refreshKeyVisto.current) return;
    refreshKeyVisto.current = refreshKey;
    void cargar();
    setVersionDeCorridas((v) => v + 1);
  }, [refreshKey, cargar]);

  async function correr(e: AppliedStrategy) {
    setCorriendo(e.id);
    try {
      const r = await strategyApplicationsApi.correrAhora(e.id);
      toast(avisoDeLaCorrida(r));
      await cargar();
      setVersionDeCorridas((v) => v + 1);
    } catch (err) {
      const respuesta = (err as { response?: { status?: number; data?: { error?: unknown } } })
        ?.response;
      // Spec 275 (R3): un 409 no es una falla: ya corría, o hay un recálculo
      // (ronda de R3: sin pintarlo de error).
      // Ronda del QA de R4: un 403 (un rol que sólo ve) tampoco es «La corrida
      // falló»: no hubo corrida. El texto del servidor, sin el rojo.
      if (respuesta?.status === 409 || respuesta?.status === 403) {
        toast({ title: 'No se corrió ahora', description: mensajeDeError(err, 'Probá de nuevo.') });
        return;
      }
      // Ronda de R3: el servidor no pudo abrir la corrida (500 con su texto). Los
      // demás 5xx siguen con el texto de siempre: puede haberse cortado a mitad.
      const noEmpezo =
        (respuesta?.status ?? 0) >= 500 && respuesta?.data?.error === NO_SE_PUDO_EMPEZAR;
      // Ronda final de R4 (QA E30): si no llegó a empezar, no es «La corrida falló»
      // (no hubo corrida): el mismo aviso que un 409, sin el rojo.
      if (noEmpezo) {
        toast({ title: 'No se corrió ahora', description: NO_SE_PUDO_EMPEZAR });
        return;
      }
      toast({
        title: 'La corrida falló',
        description: mensajeDeError(err, 'Probá de nuevo.'),
        variant: 'destructive',
      });
    } finally {
      setCorriendo(null);
    }
  }

  /** Spec 294 (D-R4-2): apagar y encender piden confirmación; el toast dice lo que pasó. */
  function alternar(e: AppliedStrategy) {
    const n = e.propuestasPendientes;
    if (e.isActive) {
      confirm({
        title: `¿Apagar «${e.name}»?`,
        description:
          n > 0
            ? `Tiene ${formatoCantidad(n)} esperando en Por decidir: ${n === 1 ? 'se retira' : 'se retiran'}. Al encenderla, su próxima corrida ${n === 1 ? 'la vuelve' : 'las vuelve'} a proponer.`
            : // Ronda final de R4: la lista puede estar vieja; el número real lo da el aviso.
              'Deja de correr hasta que la enciendas. Si tiene algo esperando en Por decidir, se retira.',
        confirmText: 'Sí, apagar',
        onConfirm: () => cambiarEncendido(e, false),
      });
    } else {
      confirm({
        title: `¿Encender «${e.name}»?`,
        // «Sólo cuando yo lo pida» no se lee bien después de «Vuelve a correr:».
        description:
          e.cadence === 'ON_DEMAND'
            ? 'Vuelve a correr cuando la revises a mano.'
            : `Vuelve a correr: ${cuando(e).toLowerCase()}.`,
        confirmText: 'Sí, encender',
        onConfirm: () => cambiarEncendido(e, true),
      });
    }
  }

  async function cambiarEncendido(e: AppliedStrategy, encender: boolean) {
    // Ronda final de R4: mientras se guarda, «Apagar»/«Encender» no se vuelven a tocar.
    setCambiando((c) => new Set(c).add(e.id));
    try {
      const r = await strategyApplicationsApi.cambiar(e.id, { isActive: encender });
      if (encender) {
        toast({
          title: `«${e.name}» quedó encendida`,
          // «Sólo cuando yo lo pida» no tiene próxima corrida: corre a mano.
          description:
            e.cadence === 'ON_DEMAND'
              ? 'Revisala a mano para que vuelva a proponer.'
              : 'Su próxima corrida vuelve a proponer lo que haga falta.',
          action: (
            <ToastAction
              altText="Revisar ahora"
              onClick={() => void correr({ ...e, isActive: true })}
            >
              Revisar ahora
            </ToastAction>
          ),
        });
      } else {
        const n = r.retiradas ?? 0;
        toast({
          title: `«${e.name}» quedó apagada`,
          description:
            (n > 0
              ? `${n === 1 ? 'Se retiró 1' : `Se retiraron ${formatoCantidad(n)}`} de Por decidir: ${n === 1 ? 'está' : 'están'} en Descartadas.`
              : 'No había nada esperando en Por decidir.') +
            (r.seDetuvoUnaCorrida ? ' La revisión que estaba corriendo se detuvo.' : ''),
        });
      }
      await cargar();
    } catch (err) {
      const texto = mensajeDeError(err, 'Probá de nuevo.');
      // Revisión final del plan 3, M2: el servidor ya la ve vencida y el
      // cliente todavía no. Se vuelve a pedir la lista, y la copia sale como de
      // una vencida (el paso 3 no dice «la original todavía tiene estos productos»).
      const vencio = /ya venci/i.test(texto);
      if (vencio) void cargar();
      const comoVencida = { ...e, closedAt: e.closedAt ?? new Date().toISOString() };
      toast({
        title: 'No se pudo cambiar',
        description: texto,
        variant: 'destructive',
        // Una vencida no se reabre (plan 2, P2-D4: «Ya venció: creá una nueva.»):
        // el aviso ofrece crearla de nuevo con todo copiado.
        ...(puedeCrear &&
          vencio && {
            action: (
              <ToastAction
                altText="Duplicar esta estrategia"
                onClick={() => setDuplicando(comoVencida)}
              >
                Duplicar
              </ToastAction>
            ),
          }),
      });
    } finally {
      setCambiando((c) => {
        const n = new Set(c);
        n.delete(e.id);
        return n;
      });
    }
  }

  function pedirBaja(e: AppliedStrategy) {
    const n = e.propuestasPendientes;
    confirm({
      title: `¿Dar de baja «${e.name}»?`,
      description:
        'Deja de correr y de proponer. Las propuestas que ya generó NO se borran: quedan en el historial con el nombre de la estrategia guardado.' +
        // Ronda del QA de R4: la baja también retira lo que espera, como apagar.
        (n > 0
          ? ` Tiene ${formatoCantidad(n)} esperando en Por decidir: ${n === 1 ? 'se retira' : 'se retiran'}.`
          : ' Si tiene algo esperando en Por decidir, se retira.') +
        // P2-D8: sin estrategia, sus vueltas no tendrían dónde resolverse.
        (e.closedAt
          ? ' Como ya venció, las vueltas que todavía esperan tu sí se descartan y esos precios quedan como están.'
          : ''),
      confirmText: 'Sí, dar de baja',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          const r = await strategyApplicationsApi.darDeBaja(e.id);
          const retiradas = r?.retiradas ?? 0;
          toast({
            title: 'Estrategia dada de baja',
            ...(retiradas > 0 && {
              description:
                retiradas === 1
                  ? 'Se retiró 1 de Por decidir: está en Descartadas.'
                  : `Se retiraron ${formatoCantidad(retiradas)} de Por decidir: están en Descartadas.`,
            }),
          });
          await cargar();
        } catch (err) {
          toast({
            title: 'No se pudo dar de baja',
            description: mensajeDeError(err, 'Probá de nuevo.'),
            variant: 'destructive',
          });
        }
      },
    });
  }

  // Ronda del QA de R4: quien no puede correr lo ve deshabilitado y con el porqué
  // (el dato del servidor; con uno viejo, el rol de la pantalla).
  const puedeCorrerLa = (e: AppliedStrategy) => e.puedeCorrer ?? puedeCrear;
  const porQueNoRevisa = (e: AppliedStrategy): string | undefined =>
    !puedeCorrerLa(e)
      ? NO_PUEDE_CORRER
      : estaVencida(e)
        ? YA_VENCIO
        : !e.isActive
          ? ESTA_APAGADA
          : undefined;
  // El `title` va también en la envoltura: el botón deshabilitado no recibe el
  // mouse (`disabled:pointer-events-none`) y su globo no se vería.
  // Ronda Y: el recorrido de la demo señala el primero («Tocá Revisar ahora»).
  const botonRevisar = (e: AppliedStrategy, chico = false, clases?: string) => (
    <span data-tour="demo-estrategia-revisar" className="inline-flex" title={porQueNoRevisa(e)}>
      <Button
        size="sm"
        variant="outline"
        className={cn('h-9 gap-1.5 rounded-full px-3.5', chico && 'h-8', clases)}
        disabled={corriendo === e.id || !e.isActive || estaVencida(e) || !puedeCorrerLa(e)}
        title={porQueNoRevisa(e)}
        onClick={() => void correr(e)}
        {...sinCambios}
      >
        {corriendo === e.id ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        ) : (
          <Play className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        Revisar ahora
      </Button>
    </span>
  );
  const menuDe = (e: AppliedStrategy) => (
    <MenuMas
      estrategia={e}
      puedeCrear={puedeCrear}
      cambiando={cambiando.has(e.id)}
      onAlternar={() => void alternar(e)}
      onPermiso={() => setPermisoDe(e)}
      onDuplicar={() => setDuplicando(e)}
      onBaja={() => pedirBaja(e)}
    />
  );
  const metodoDe = (e: AppliedStrategy) =>
    `${METODOS.find((m) => m.metodo === e.strategy)?.titulo ?? 'Fórmula propia'}${
      configTexto(e) ? ` · ${configTexto(e)}` : ''
    }`;
  // Lote B2 (punto 6): el «N productos» de la tarjeta abre todos los que alcanza.
  const verProductos = (e: AppliedStrategy, texto: string, ariaLabel?: string) => (
    <button
      type="button"
      aria-label={ariaLabel}
      className="rounded-sm text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={() => setProductosDe(e)}
    >
      {texto}
    </button>
  );

  const abiertaLa = abierta ? (lista.find((x) => x.id === abierta.id) ?? null) : null;
  // El pie de la tarjeta (maqueta): cuántos precios esperan, de todas las estrategias.
  const esperanEnTotal = lista.reduce((s, e) => s + e.propuestasPendientes, 0);
  // Revisión final de rama (F3): «Volver a intentar» con la misma condición que
  // «Revisar ahora» (apagada o vencida no corre).
  const puedeReintentar = (id: string) => {
    const e = lista.find((x) => x.id === id);
    return e != null && e.isActive && !estaVencida(e);
  };
  // Revisión final de rama (F8): de dónde se abrió la vista, para devolverle el
  // foco al volver (el botón se desmonta mientras la vista está abierta; se
  // busca el nuevo por `data-foco`). Sin marca (la tira), el «Ver corridas →»
  // de su tarjeta.
  const volverElFocoA = useRef<string | null>(null);
  const abrirLaEstrategia = (id: string, ev: { id: string; cuando: string } | null = null) => {
    const origen = document.activeElement?.getAttribute('data-foco');
    volverElFocoA.current = origen ?? `ver-corridas-${id}`;
    setAbierta({ id, resaltar: ev?.id ?? null, cuando: ev?.cuando ?? null });
  };
  useEffect(() => {
    if (abierta || !volverElFocoA.current) return;
    const marca = volverElFocoA.current;
    volverElFocoA.current = null;
    document.querySelector<HTMLElement>(`[data-foco="${CSS.escape(marca)}"]`)?.focus();
  }, [abierta]);

  return (
    <div className="space-y-4" data-testid="pestana-estrategias">
      {abiertaLa ? (
        <VistaDeLaEstrategia
          key={abiertaLa.id}
          estrategia={{ id: abiertaLa.id, nombre: abiertaLa.name }}
          estado={{
            texto: estaVencida(abiertaLa) ? 'Venció' : abiertaLa.isActive ? 'Activa' : 'Apagada',
            tono: !estaVencida(abiertaLa) && abiertaLa.isActive ? 'ok' : 'neutro',
          }}
          esperan={abiertaLa.propuestasPendientes}
          datos={[
            { k: 'Cómo calcula', v: metodoDe(abiertaLa) },
            { k: 'A quién alcanza', v: aQuienEnLaTarjeta(abiertaLa) },
            {
              k: 'Permiso',
              v: permisoEnPalabras(abiertaLa.mode, abiertaLa.askAboveChangePct ?? null),
            },
            { k: 'Cada cuánto revisa', v: cuando(abiertaLa) },
          ]}
          acciones={
            <>
              {botonRevisar(abiertaLa)}
              {menuDe(abiertaLa)}
            </>
          }
          resaltar={abierta?.resaltar ?? null}
          resaltarCuando={abierta?.cuando ?? null}
          version={versionDeCorridas}
          puedeCorrer={puedeCorrerLa(abiertaLa)}
          puedeReintentar={puedeReintentar}
          onVolver={() => setAbierta(null)}
          onIrAPorDecidir={onIrA ? () => onIrA('BANDEJA', abiertaLa.id) : undefined}
          onVolverAIntentar={() => void correr(abiertaLa)}
          onIrA={onIrA}
          historial={
            <HistorialDeCambios
              pedir={(antesDe) => strategyApplicationsApi.historial(abiertaLa.id, antesDe)}
              enPalabras={(e, todas) =>
                historialDeEstrategiaEnPalabras(
                  e,
                  fotosCompletas(todas, fotoDeHoy(abiertaLa)).get(e.id)
                )
              }
              version={versionDeCorridas + versionDeLaLista}
            />
          }
        />
      ) : (
        <>
          {/* Compite (rediseño del 14-oct): lo que hace una estrategia lo dice el
              encabezado de la página; la hora de Bolivia va al pie de la tarjeta. */}
          {cargando ? (
            <div className="space-y-3" aria-busy="true" aria-label="Cargando las estrategias">
              {Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} className="h-28 w-full rounded-tarjeta" />
              ))}
            </div>
          ) : error ? (
            <EstadoError onReintentar={() => void cargar()} className="rounded-tarjeta">
              {error}
            </EstadoError>
          ) : lista.length === 0 ? (
            <EstadoVacio
              icono={Castle}
              titulo="Todavía no hay ninguna estrategia"
              className="rounded-tarjeta bg-card py-10"
              accion={
                puedeCrear ? (
                  <Button
                    className={cn('h-10 gap-1.5 rounded-full px-5', BOTON_DEGRADADO)}
                    onClick={() => setAbierto(true)}
                    {...sinCambios}
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    Crear la primera
                  </Button>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Crear estrategias es para quien administra la empresa o aprueba precios.
                  </p>
                )
              }
            >
              Elegí a qué productos alcanza y cómo calcula el precio. La primera puede quedar en
              «siempre preguntame», así nada cambia sin tu aprobación.
            </EstadoVacio>
          ) : (
            <>
              {/* Arriba, cómo ver y el botón principal de la pantalla (uno solo). */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Segmentado
                  aria-label="Cómo ver"
                  valor={vista}
                  onCambiar={(v) => {
                    setVista(v);
                    guardarVista(v);
                  }}
                  opciones={[
                    { valor: 'tarjetas', texto: 'Tarjetas', icono: LayoutGrid },
                    { valor: 'lista', texto: 'Lista', icono: List },
                    { valor: 'fechas', texto: 'Por fechas', icono: CalendarDays },
                  ]}
                />
                {puedeCrear &&
                  (() => {
                    const boton = (
                      <Button
                        onClick={() => setAbierto(true)}
                        {...sinCambios}
                        className={cn(
                          'h-11 shrink-0 gap-2 rounded-full px-6 text-base font-semibold',
                          BOTON_DEGRADADO
                        )}
                      >
                        <Plus className="h-4 w-4" aria-hidden="true" />
                        Crear una estrategia
                      </Button>
                    );
                    // En Compite, al encabezado de la página (la maqueta); suelta, acá.
                    if (!enCompite) return boton;
                    return destinoDeLaAccion ? createPortal(boton, destinoDeLaAccion) : null;
                  })()}
              </div>

              {vista === 'fechas' ? (
                <VistaPorFechas
                  estrategias={lista.map((e) => ({ id: e.id, nombre: e.name }))}
                  filtro={filtroPorFechas}
                  onCambiarFiltro={setFiltroPorFechas}
                  version={versionDeCorridas}
                  puedeCorrer={puedeCrear}
                  puedeReintentar={puedeReintentar}
                  onVolverAIntentar={(id) => {
                    const e = lista.find((x) => x.id === id);
                    if (e) void correr(e);
                  }}
                  onIrA={onIrA}
                />
              ) : (
                <Tarjeta className="overflow-hidden">
                  {/* Agregados del 29-sep (A1): buscar, filtrar y ordenar (maqueta Compite). */}
                  <div
                    className="flex flex-wrap items-center gap-2.5 px-5 py-3.5"
                    data-testid="controles-de-estrategias"
                  >
                    <div className="relative w-52 sm:w-64">
                      <Search
                        aria-hidden="true"
                        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                      />
                      <Input
                        type="search"
                        className="h-9 rounded-xl bg-muted/40 pl-9"
                        placeholder="Buscar por nombre"
                        aria-label="Buscar una estrategia por nombre"
                        value={filtro.q}
                        onChange={(ev) => cambiarFiltro({ q: ev.target.value })}
                      />
                    </div>
                    <Pildoras
                      aria-label="Estado"
                      valor={filtro.estado}
                      onCambiar={(estado) => cambiarFiltro({ estado })}
                      opciones={[
                        { valor: 'TODAS', texto: 'Todas', cuenta: porEstado.TODAS },
                        { valor: 'ACTIVA', texto: 'Activas', cuenta: porEstado.ACTIVA },
                        { valor: 'APAGADA', texto: 'Apagadas', cuenta: porEstado.APAGADA },
                        { valor: 'VENCIDA', texto: 'Vencidas', cuenta: porEstado.VENCIDA },
                      ]}
                    />
                    {/* Lote B2 (punto 7): el calendario de dos meses, en hora de Bolivia. */}
                    <SelectorDeFechas
                      etiqueta="Creada"
                      valor={filtro.creada}
                      onCambiar={(creada) => cambiarFiltro({ creada })}
                    />
                    <label className="ml-auto flex items-center gap-2 text-sm text-muted-foreground">
                      Orden
                      <SelectNativo
                        aria-label="Ordenar"
                        className="w-auto rounded-xl font-medium"
                        value={filtro.orden}
                        onChange={(ev) =>
                          cambiarFiltro({ orden: ev.target.value as OrdenDeEstrategias })
                        }
                      >
                        <option value="reciente">Las más nuevas primero</option>
                        <option value="vieja">Las más viejas primero</option>
                        <option value="nombre">Por nombre</option>
                        <option value="esperan">Las que más esperan primero</option>
                      </SelectNativo>
                    </label>
                  </div>

                  {visibles.length === 0 ? (
                    <div className="border-t border-border px-5 py-5">
                      <EstadoVacio
                        titulo="Ninguna estrategia con ese filtro"
                        className="rounded-tarjeta"
                        accion={
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full"
                            onClick={() =>
                              setFiltro((f) => ({ ...FILTRO_INICIAL, orden: f.orden }))
                            }
                          >
                            Limpiar filtros
                          </Button>
                        }
                      >
                        Probá con otro nombre u otras fechas.
                      </EstadoVacio>
                    </div>
                  ) : vista === 'lista' ? (
                    <div className="border-t border-border">
                      <Table className="min-w-[880px] table-fixed">
                        <colgroup>
                          {[31, 9, 15, 13, 13, 19].map((w, i) => (
                            <col key={i} style={{ width: `${w}%` }} />
                          ))}
                        </colgroup>
                        <TableHeader className="bg-muted/50">
                          <TableRow className="hover:bg-transparent">
                            <TableHead className="h-10 min-w-0 text-xs font-semibold pl-5 [overflow-wrap:anywhere]">
                              Estrategia
                            </TableHead>
                            <TableHead className="h-10 min-w-0 text-xs font-semibold whitespace-nowrap text-right">
                              Productos
                            </TableHead>
                            <TableHead className="h-10 min-w-0 text-xs font-semibold [overflow-wrap:anywhere]">
                              Última corrida <span className="font-normal">· hora de Bolivia</span>
                            </TableHead>
                            <TableHead className="h-10 min-w-0 text-xs font-semibold whitespace-nowrap">
                              Esperan
                            </TableHead>
                            <TableHead className="h-10 min-w-0 text-xs font-semibold whitespace-nowrap">
                              Estado
                            </TableHead>
                            <TableHead className="h-10 min-w-0 pr-5">
                              <span className="sr-only">Acciones</span>
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {visibles.map((e) => {
                            const vencida = estaVencida(e);
                            return (
                              <TableRow
                                key={e.id}
                                data-testid="fila-estrategia"
                                aria-label={e.name}
                                className={cn(estadoDeLaEstrategia(e) !== 'ACTIVA' && 'opacity-80')}
                              >
                                <TableCell className="min-w-0 py-3.5 pl-5 [overflow-wrap:anywhere]">
                                  {/* En la lista, «Qué hizo» se abre desde el nombre (maqueta A1). */}
                                  <button
                                    type="button"
                                    className="min-w-0 rounded-sm text-left font-semibold text-foreground [overflow-wrap:anywhere] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    onClick={() => setFichaDe(e)}
                                  >
                                    {e.name}
                                  </button>
                                  {/* Como la maqueta: el método y el permiso, debajo del nombre (decisión A1:
                                        se siguen viendo, y la tabla entra a 1280 px sin moverse de costado). */}
                                  <p className="mt-1 text-sm text-muted-foreground [overflow-wrap:anywhere]">
                                    <span className="text-foreground">{metodoDe(e)}</span> ·{' '}
                                    {permisoEnPalabras(e.mode, e.askAboveChangePct ?? null)}
                                  </p>
                                  <p className="mt-0.5 text-xs text-muted-foreground [overflow-wrap:anywhere]">
                                    Creada {e.createdAt ? `el ${formatoFecha(e.createdAt)}` : ''}
                                    {e.creadaPor && (
                                      <>
                                        {' '}
                                        <span>
                                          por {e.creadaPor}
                                          {e.creadaPorSoporte ? ', del soporte de Prixia' : ''}
                                        </span>
                                      </>
                                    )}
                                  </p>
                                </TableCell>
                                <TableCell className="tabular-nums min-w-0 py-3.5 text-right font-semibold [overflow-wrap:anywhere]">
                                  {/* Ronda final B2: los de hoy, como la tarjeta, y el número dice
                                  de qué estrategia es para quien no ve la fila. */}
                                  {verProductos(
                                    e,
                                    e.group.kind === 'MANUAL'
                                      ? formatoCantidad(e.productosHoy ?? e.group.skus.length)
                                      : e.productosHoy != null
                                        ? formatoCantidad(e.productosHoy)
                                        : '—',
                                    // Ronda 2 B2: «Ver los productos de «Estrategia»» — con
                                    // «N productos de la marca X de «Estrategia»» quedaban dos «de».
                                    `Ver los productos de «${e.name}»`
                                  )}
                                </TableCell>
                                <TableCell className="min-w-0 py-3.5 text-sm [overflow-wrap:anywhere]">
                                  <div className="text-foreground">
                                    {e.lastRunAt ? formatoFechaYHora(e.lastRunAt) : '—'}
                                  </div>
                                  {/* Spec 275 (R2): las corridas, al lado de la última. */}
                                  <button
                                    type="button"
                                    className="rounded-sm text-xs font-semibold text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    data-foco={`ver-corridas-${e.id}`}
                                    onClick={() => abrirLaEstrategia(e.id)}
                                  >
                                    Ver corridas →
                                  </button>
                                </TableCell>
                                <TableCell className="min-w-0 py-3.5 [overflow-wrap:anywhere]">
                                  {e.propuestasPendientes > 0 ? (
                                    onIrA ? (
                                      // Lleva a Revisar precios ya filtrado por esta estrategia.
                                      <button
                                        type="button"
                                        className="tabular-nums inline-flex items-center whitespace-nowrap rounded-full bg-alerta-suave px-2.5 py-1 text-xs font-semibold text-alerta transition hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                        onClick={() => onIrA('BANDEJA', e.id)}
                                      >
                                        {formatoCantidad(e.propuestasPendientes)} por decidir
                                        <span className="sr-only">
                                          : {e.propuestasPendientes === 1 ? 'abrirlo' : 'abrirlos'}{' '}
                                          en Revisar precios
                                        </span>
                                      </button>
                                    ) : (
                                      <span className="tabular-nums inline-flex whitespace-nowrap rounded-full bg-alerta-suave px-2.5 py-1 text-xs font-semibold text-alerta">
                                        {formatoCantidad(e.propuestasPendientes)} por decidir
                                      </span>
                                    )
                                  ) : (
                                    <span className="text-muted-foreground">—</span>
                                  )}
                                </TableCell>
                                <TableCell className="min-w-0 py-3.5 [overflow-wrap:anywhere]">
                                  {/* Maqueta: el interruptor a la vista. Apagar y encender
                                      piden confirmación, igual que desde «Más»; el estado
                                      va en palabras, no sólo en el color. Una vencida no
                                      se enciende (Nº 9). */}
                                  <div className="flex flex-wrap items-center gap-2">
                                    {!vencida && (
                                      <Switch
                                        checked={e.isActive}
                                        disabled={cambiando.has(e.id)}
                                        onCheckedChange={() => alternar(e)}
                                        aria-label={`Encender o apagar «${e.name}»`}
                                      />
                                    )}
                                    <Etiqueta
                                      tono={estadoDeLaEstrategia(e) === 'ACTIVA' ? 'ok' : 'neutro'}
                                      className="whitespace-normal"
                                    >
                                      {vencida ? 'Venció' : e.isActive ? 'Activa' : 'Apagada'}
                                    </Etiqueta>
                                  </div>
                                </TableCell>
                                <TableCell className="min-w-0 py-3.5 pr-5 [overflow-wrap:anywhere]">
                                  <div className="flex flex-wrap items-center justify-end gap-1">
                                    {botonRevisar(e, true, 'whitespace-nowrap')}
                                    {menuDe(e)}
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    // Una debajo de la otra, a todo el ancho: en dos columnas los números de la
                    // corrida no entran y se parten en tres líneas (maqueta del pulido).
                    <div className="space-y-3 border-t border-border bg-muted/30 p-3 sm:p-4">
                      {visibles.map((e) => (
                        <article
                          key={e.id}
                          data-testid="tarjeta-estrategia"
                          aria-label={e.name}
                          className={cn(
                            'space-y-3 rounded-tarjeta border border-border bg-card p-5 shadow-sm',
                            estadoDeLaEstrategia(e) !== 'ACTIVA' && 'opacity-70'
                          )}
                        >
                          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
                            <div className="min-w-0 flex-1">
                              <h3 className="flex min-w-0 flex-wrap items-center gap-2">
                                <button
                                  type="button"
                                  className="tracking-tight min-w-0 rounded-sm text-left text-lg font-semibold text-foreground [overflow-wrap:anywhere] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                  data-foco={`nombre-${e.id}`}
                                  onClick={() => abrirLaEstrategia(e.id)}
                                >
                                  {e.name}
                                </button>
                                {/* Nº 9: una vencida no dice «Apagada» (la ficha tampoco). */}
                                <Etiqueta tono={!estaVencida(e) && e.isActive ? 'ok' : 'neutro'}>
                                  {estaVencida(e) ? 'Venció' : e.isActive ? 'Activa' : 'Apagada'}
                                </Etiqueta>
                              </h3>
                              {/* Una sola línea: método · número · permiso · a quién · cada cuánto. */}
                              <p className="mt-1 text-sm text-muted-foreground">
                                {/* Nº 10: el mismo nombre que el asistente y la ficha. */}
                                <span>
                                  {metodoDe(e)} ·{' '}
                                  {permisoEnPalabras(e.mode, e.askAboveChangePct ?? null)}
                                </span>
                                {' · '}
                                {verProductos(e, aQuienEnLaTarjeta(e))}
                                {' · '}
                                <span>{cuando(e)}</span>
                                {/* A1: sin fecha (un servidor viejo) no se inventa. */}
                                {e.createdAt && (
                                  <span> · creada el {formatoFecha(e.createdAt)}</span>
                                )}
                              </p>
                              {/* Spec 294 (D-R4-5): quién la creó y quién la cambió por última vez. */}
                              {lineaDeQuien(e) && (
                                <p className="mt-0.5 min-w-0 text-xs text-muted-foreground [overflow-wrap:anywhere]">
                                  {lineaDeQuien(e)}
                                </p>
                              )}
                            </div>

                            {/* Dos acciones a la vista; el resto, en «Más» (decisión 2 de la maqueta). */}
                            <div className="flex shrink-0 items-center gap-1.5">
                              {botonRevisar(e)}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-9 rounded-full px-3.5"
                                onClick={() => setFichaDe(e)}
                              >
                                Qué hizo
                              </Button>
                              {menuDe(e)}
                            </div>
                          </div>

                          {e.propuestasPendientes > 0 &&
                            (() => {
                              // Compite (rediseño del 14-oct): el lugar se llama Revisar precios
                              // (antes «Por decidir», que hoy es sólo su pestaña de pendientes).
                              const etiqueta = (
                                <Etiqueta
                                  tono="alerta"
                                  data-testid="esperan-en-por-decidir"
                                  className="rounded-full px-2.5 py-1 text-sm"
                                >
                                  {formatoCantidad(e.propuestasPendientes)}{' '}
                                  {e.propuestasPendientes === 1 ? 'espera' : 'esperan'} tu decisión
                                  en Revisar precios →
                                </Etiqueta>
                              );
                              return onIrA ? (
                                <button
                                  type="button"
                                  className="inline-block rounded-full text-left transition hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                  onClick={() => onIrA('BANDEJA', e.id)}
                                >
                                  {etiqueta}
                                </button>
                              ) : (
                                etiqueta
                              );
                            })()}

                          {estaVencida(e) && (
                            <p data-testid="vencida" className="text-xs text-muted-foreground">
                              Ya venció: creá una nueva.{' '}
                              {puedeCrear && (
                                <Button
                                  size="sm"
                                  variant="link"
                                  className="h-auto p-0 text-xs"
                                  onClick={() => setDuplicando(e)}
                                  {...sinCambios}
                                >
                                  Duplicarla
                                </Button>
                              )}
                            </p>
                          )}

                          {/* Lote B2 (punto 6): cómo toma los productos, en una línea. */}
                          <p data-testid="como-toma" className="text-xs text-muted-foreground">
                            {comoTomaEnLaTarjeta(e)}
                          </p>

                          <ResumenCorrida estrategia={e} compacta />

                          {/* Spec 275 (R2): las últimas 7 corridas y «Ver corridas →». */}
                          <div className="flex flex-wrap items-end justify-between gap-3 border-t border-border pt-3">
                            <TiraDeCorridas
                              eventos={ultimas[e.id] ?? []}
                              onElegir={(ev) => abrirLaEstrategia(e.id, ev)}
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 rounded-full"
                              data-foco={`ver-corridas-${e.id}`}
                              onClick={() => abrirLaEstrategia(e.id)}
                            >
                              Ver corridas →
                            </Button>
                          </div>
                        </article>
                      ))}
                    </div>
                  )}

                  {/* El pie de la maqueta: la hora y, si hay, a dónde ir a decidir. */}
                  <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 border-t border-border px-5 py-3 text-sm text-muted-foreground">
                    <span>Fechas y horas en hora de Bolivia</span>
                    {esperanEnTotal > 0 && (
                      <>
                        <span aria-hidden="true">·</span>
                        {onIrA ? (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 rounded-sm font-semibold text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            onClick={() => onIrA('BANDEJA', null)}
                          >
                            <span className="text-alerta">
                              {cantidadDe(esperanEnTotal, 'precio espera', 'precios esperan')}
                            </span>{' '}
                            tu decisión en Revisar precios
                            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                          </button>
                        ) : (
                          <span>
                            {cantidadDe(esperanEnTotal, 'precio espera', 'precios esperan')} tu
                            decisión
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </Tarjeta>
              )}
            </>
          )}
        </>
      )}

      <NuevaEstrategiaDialog
        abierto={abierto}
        onCerrar={() => setAbierto(false)}
        onCreada={async () => {
          setAbierto(false);
          await cargar();
        }}
      />
      <CambiarPermisoDialog
        estrategia={permisoDe}
        onCerrar={() => setPermisoDe(null)}
        onGuardado={async () => {
          setPermisoDe(null);
          await cargar();
        }}
      />
      <FichaEstrategiaDialog estrategia={fichaDe} onCerrar={() => setFichaDe(null)} />
      <ProductosDeLaEstrategiaDialog
        estrategia={productosDe}
        subtitulo={
          productosDe
            ? `${metodoDe(productosDe)} · ${permisoEnPalabras(productosDe.mode, productosDe.askAboveChangePct ?? null)} · ${cuando(productosDe)}`
            : ''
        }
        onCerrar={() => setProductosDe(null)}
        onIrAPorDecidir={
          onIrA
            ? (id, productId) => {
                setProductosDe(null);
                // Lote C2b: directo a la fila abierta de ese producto.
                onIrA('BANDEJA', id, { productId });
              }
            : undefined
        }
      />
      <NuevaEstrategiaDialog
        abierto={duplicando != null}
        inicial={duplicando ? borradorDesdeLaEstrategia(duplicando) : null}
        duplicaDe={
          duplicando
            ? {
                nombre: duplicando.name,
                noSeCopia: loQueNoSeCopia(duplicando),
                // P3-D8: se duplica también una activa; el paso 3 lo avisa.
                // P2-D7 (re-auditoría del 27-sep noche, B4): una apagada no vencida
                // también sigue siendo dueña de sus productos.
                originalActiva: !estaVencida(duplicando),
              }
            : null
        }
        onCerrar={() => setDuplicando(null)}
        onCreada={async () => {
          setDuplicando(null);
          await cargar();
        }}
      />
      <ConfirmDialog />
    </div>
  );
}

/**
 * El menú «Más» de una tarjeta (decisión 2 de la maqueta del pulido): Apagar ·
 * Cambiar permiso · Duplicar · — · Dar de baja, en rojo y separado al final.
 * Son las mismas acciones de siempre, con sus mismas condiciones: una vencida no
 * se prende ni cambia el permiso (Nº 9 y revisión final del plan 3), y duplicar
 * pide un rol que pueda crear (D5). `modal={false}`: el menú abre un diálogo
 * (permiso, confirmar la baja) y, modal, Radix dejaba la página sin clics.
 */
function MenuMas({
  estrategia: e,
  puedeCrear,
  cambiando = false,
  onAlternar,
  onPermiso,
  onDuplicar,
  onBaja,
}: {
  estrategia: AppliedStrategy;
  puedeCrear: boolean;
  /** Ronda final de R4: se está guardando un apagar/encender de esta estrategia. */
  cambiando?: boolean;
  onAlternar: () => void;
  onPermiso: () => void;
  onDuplicar: () => void;
  onBaja: () => void;
}) {
  const vencida = estaVencida(e);
  const hayArriba = !vencida || puedeCrear;
  const soloMira = useSoporteSoloMira();
  return (
    <DropdownMenu modal={false}>
      {/* Compite (rediseño del 14-oct): el ⋮ de la maqueta; el nombre sigue diciendo de cuál. */}
      <DropdownMenuTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          className="h-9 w-9 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
          aria-label={`Más acciones de «${e.name}»`}
          title="Más acciones"
        >
          <MoreVertical aria-hidden="true" className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[180px]">
        {soloMira && (
          <p role="status" className="max-w-[220px] px-2 py-1.5 text-xs text-muted-foreground">
            {AVISO_SOLO_MIRA}
          </p>
        )}
        {!vencida && (
          <DropdownMenuItem
            onSelect={onAlternar}
            disabled={cambiando || soloMira}
            className="gap-2"
          >
            <Power aria-hidden="true" className="h-3.5 w-3.5" />
            {e.isActive ? 'Apagar' : 'Encender'}
          </DropdownMenuItem>
        )}
        {!vencida && (
          <DropdownMenuItem onSelect={onPermiso} disabled={soloMira}>
            Cambiar permiso
          </DropdownMenuItem>
        )}
        {puedeCrear && (
          <DropdownMenuItem onSelect={onDuplicar} disabled={soloMira} className="gap-2">
            <Copy aria-hidden="true" className="h-3.5 w-3.5" />
            Duplicar
          </DropdownMenuItem>
        )}
        {hayArriba && <DropdownMenuSeparator data-testid="separador-de-la-baja" />}
        <DropdownMenuItem
          onSelect={onBaja}
          disabled={soloMira}
          className="gap-2 text-bloqueo focus:text-bloqueo"
        >
          <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
          Dar de baja
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Editar el permiso de una estrategia ya creada (S5). `AUTO_WITH_RULES` se lee como AUTO. */
function CambiarPermisoDialog({
  estrategia,
  onCerrar,
  onGuardado,
}: {
  estrategia: AppliedStrategy | null;
  onCerrar: () => void;
  onGuardado: () => void | Promise<void>;
}) {
  const { toast } = useToast();
  const inicial: Permiso = estrategia
    ? {
        mode: estrategia.mode === 'SUPERVISED' ? 'SUPERVISED' : 'AUTO',
        askAboveChangePct: estrategia.askAboveChangePct ?? null,
      }
    : PERMISO_INICIAL;
  const [permiso, setPermiso] = useState<Permiso | null>(inicial);
  const [guardando, setGuardando] = useState(false);
  const sinCambios = atributosSoloMira(useSoporteSoloMira());

  useEffect(() => {
    setPermiso(inicial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estrategia?.id]);

  async function guardar() {
    if (!estrategia || !permiso) return;
    setGuardando(true);
    try {
      await strategyApplicationsApi.cambiar(estrategia.id, {
        mode: permiso.mode,
        askAboveChangePct: permiso.askAboveChangePct,
      });
      toast({
        title: 'Permiso guardado',
        description: permisoEnPalabras(permiso.mode, permiso.askAboveChangePct),
      });
      await onGuardado();
    } catch (e) {
      toast({
        title: 'No se pudo guardar',
        description: mensajeDeError(e, 'Probá de nuevo.'),
        variant: 'destructive',
      });
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open={estrategia !== null} onOpenChange={(v) => !v && onCerrar()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Permiso de «{estrategia?.name}»</DialogTitle>
          <DialogDescription>Qué hace cuando propone un precio.</DialogDescription>
        </DialogHeader>
        {estrategia && (
          <PermisoDeEstrategia key={estrategia.id} value={inicial} onChange={setPermiso} />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button disabled={!permiso || guardando} onClick={() => void guardar()} {...sinCambios}>
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Lo que dice «Revisar ahora» al terminar. Revisión final del plan 3: I3, las
 * pendientes que ya esperaban no son «el mismo precio que ya tenían» (hay N
 * esperando con otro precio); I4, si el tope por corrida la cortó, cuántos
 * quedaron para la próxima (van primero).
 */
function avisoDeLaCorrida(r: RunSummary): { title: string; description: string } {
  // Ronda del QA de R4 (R4-6): la apagaron (o la dieron de baja) mientras corría:
  // lo que propuso se retiró; no «Están en Por decidir».
  if (r.cortadaPorApagado || r.cortadaPor === 'APAGADA')
    return {
      title: 'Se apagó mientras corría',
      description: 'Lo que había propuesto se retiró de Por decidir.',
    };
  if (r.cortadaPor === 'BAJA')
    return {
      title: 'Se dio de baja mientras corría',
      description: 'Lo que había propuesto se retiró de Por decidir.',
    };
  const yaEsperaban = r.yaEsperaban ?? 0;
  const quedaron = r.pendientes ?? 0;
  const cola =
    quedaron > 0
      ? quedaron === 1
        ? 'quedó 1 para la próxima'
        : `quedaron ${formatoCantidad(quedaron)} para la próxima`
      : null;
  const conCola = (texto: string) => (cola ? `${texto} · ${cola}` : texto);
  if (r.propuestas > 0) {
    const s = r.propuestas === 1 ? '' : 's';
    return {
      title: `${formatoCantidad(r.propuestas)} propuesta${s} nueva${s}`,
      description: conCola(
        yaEsperaban > 0
          ? `Están en «Por decidir» · ${yaEsperabanEnPalabras(yaEsperaban)}`
          : 'Están en «Por decidir»'
      ),
    };
  }
  return {
    title: yaEsperaban > 0 ? 'Nada nuevo que proponer' : 'No hubo nada que proponer',
    description: conCola(detalleSinPropuestas(r.evaluados, r.sinDatos, r.silenciados, yaEsperaban)),
  };
}

function yaEsperabanEnPalabras(n: number): string {
  return n === 1
    ? '1 ya esperaba en «Por decidir»'
    : `${formatoCantidad(n)} ya esperaban en «Por decidir»`;
}

function detalleSinPropuestas(
  evaluados: number,
  sinDatos: number,
  silenciados: number,
  yaEsperaban: number
): string {
  if (evaluados === 0 && sinDatos === 0 && silenciados === 0)
    return 'Ninguno de sus productos tiene novedades desde la última revisión.';
  const partes: string[] = [];
  if (yaEsperaban > 0) partes.push(yaEsperabanEnPalabras(yaEsperaban));
  if (silenciados > 0)
    partes.push(`${formatoCantidad(silenciados)} ya los habías descartado con ese mismo precio`);
  // C2/H15 (27-sep): no siempre es la competencia (bajar un %, apuntar a un margen).
  if (sinDatos > 0)
    partes.push(
      `a ${formatoCantidad(sinDatos)} no se les pudo calcular: les falta competencia, costo o precio de lista, según el método`
    );
  if (partes.length === 0) return 'El precio propuesto era el mismo que ya tenían.';
  return partes.join(' · ');
}

export default AppliedStrategiesTab;
